import React from "react";
import { Modal } from "@mantine/core";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../types/transactions";
import { BusinessInfo } from "../../../types/context";
import CheckoutPanel from "./CheckoutPanel";
import { useCheckoutState } from "./useCheckoutState";

interface CheckoutScreenModalProps {
  opened: boolean;
  /** Back / X / Cancel Checkout all leave via this handler. */
  onClose: () => void;
  transaction: GetTransactionsResponse | TransactionResponse;
  remainingBalance: number;
  businessInfo: BusinessInfo;
  onTransactionUpdated: (
    updatedTransaction: GetTransactionsResponse | TransactionResponse,
  ) => void;
  onRefetchTransaction?: () => Promise<void>;
  /** Package-sale passthrough (QuickSale 100%-upfront flow). */
  amountDue?: number;
  amountNote?: string;
  packageSaleName?: string;
}

/**
 * Standalone fullscreen host for the redesigned checkout screen — used
 * where checkout isn't an in-place swap inside an existing fullscreen
 * surface (QuickSale product + package-sale flows). Owns its own
 * checkout-selection state, which resets when the modal closes.
 */
export default function CheckoutScreenModal({
  opened,
  onClose,
  transaction,
  remainingBalance,
  businessInfo,
  onTransactionUpdated,
  onRefetchTransaction,
  amountDue,
  amountNote,
  packageSaleName,
}: CheckoutScreenModalProps) {
  const { state, actions } = useCheckoutState();

  const handleExit = () => {
    actions.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleExit}
      fullScreen
      withCloseButton={false}
      closeOnEscape={false}
      closeOnClickOutside={false}
      styles={{
        body: { padding: 0, backgroundColor: "var(--surface-page)" },
      }}
    >
      <CheckoutPanel
        transaction={transaction}
        remainingBalance={remainingBalance}
        businessInfo={businessInfo}
        state={state}
        actions={actions}
        onTransactionUpdated={onTransactionUpdated}
        onRefetchTransaction={onRefetchTransaction}
        onBack={handleExit}
        onCancelCheckout={handleExit}
        onCloseModal={handleExit}
        onPaymentComplete={handleExit}
        amountDue={amountDue}
        amountNote={amountNote}
        packageSaleName={packageSaleName}
      />
    </Modal>
  );
}
